import React, { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import CustomTabPanel from "components/Shared/CustomTabPanel";
import CustomTabs from "components/Shared/CustomTabs";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import NoteTab from "../../Note/Notetab";

import { useGetNotesBySourceQuery } from "store/services/noteApi";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";

import ShipmentHistoryTab from "./ShipmentHistoryTab";
import ShipmentProcessTab from "./ShipmentProcessTab";
import ShipmentRoutesTab from "./ShipmentRoutesTab";
import DocumentsTab from "../../Document/DocumentTab";
import { useGetDocumentsBySourceQuery } from "store/services/documentApi";
import { useAuth } from "hooks";
import ShipmentPricesTab from "./ShipmentPricingTab";
import ShipmentActionTab from "./ShipmentActionTab";
import ShipmentItemsTab from "./ShipmentItemsTab";
import ShipmentRefundTab from "../../Payment/ShipmentRefund/ShipmentRefundComponents/ShipmentRefundTab";

import ShipmentSmsLogTab from "./ShipmentSmsLogTab";
import { useGetShipmentMessagesByShipmentIdQuery } from "store/services/smsLogListApi";
import { ShipmentChangeLogs } from "./ShipmentChangeLogs";
import ShipmentTransitBranchTab from "./ShipmentTransitBranchTab";
import { useLazyGetShipmentLogsQuery, useLazyListShipmentPricesQuery, useLazyListShipmentRoutesQuery, useLazyListShipmentTransitBranchesQuery } from "store/services/shipmentApi";
import { useLazyGetShipmentActionListQuery } from "store/services/shipmentActionApi";
import { useGetShipmentRefundDetailQuery } from "store/services/PaymentsApi/shipmentRefundApi";

const ShipmentDetailTab = () => {
  const { id } = useParams();
  const { isCustomer, isVendor } = useAuth();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "note";
  const { ShipmentDetail } = useShipmentDetailContext();

  const { data: shipmentRefundDetail } = useGetShipmentRefundDetailQuery(
    ShipmentDetail?._id,
    {
      skip: !ShipmentDetail?._id,
    },
  );

  // Fetch notes to get the count for the badge
  const { data: notes } = useGetNotesBySourceQuery(
    { source: "Shipment", sourceRef: ShipmentDetail?._id },
    { skip: !ShipmentDetail?._id }
  );

  const filteredNotes = Array.isArray(notes)
    ? notes.filter((note) => (isCustomer || isVendor ? note.showClient : true))
    : [];

  const [fetchRoutes, { data: routesRes }] = useLazyListShipmentRoutesQuery();
  const [fetchActions, { data: actionsRes }] = useLazyGetShipmentActionListQuery();
  const [fetchChangeLogs, { data: changeLogsRes }] = useLazyGetShipmentLogsQuery();
  const [transitBranchLogs, { data: transitBranchRes }] = useLazyListShipmentTransitBranchesQuery();

  useEffect(() => {
    if (ShipmentDetail?._id) {
      fetchRoutes({ shipmentId: ShipmentDetail._id });
      fetchActions({ shipmentId: ShipmentDetail._id });
      fetchChangeLogs(ShipmentDetail._id);
      transitBranchLogs({ shipmentId: ShipmentDetail._id });
    }
  }, [ShipmentDetail?._id, fetchRoutes, fetchActions, fetchChangeLogs, transitBranchLogs]);

  const routeCount = routesRes?.totalItems ?? routesRes?.data?.length ?? 0;
  const actionCount = actionsRes?.totalItems ?? actionsRes?.data?.length ?? 0;
  const changeLogCount = changeLogsRes?.data?.details?.length ?? changeLogsRes?.totalItems ?? 0;
  const transitBranchCount = transitBranchRes?.data?.details?.length ?? transitBranchRes?.totalItems ?? 0;


  // Fetch documents to get the count for the badge
  const { data: documents } = useGetDocumentsBySourceQuery(
    { source: "Shipment", sourceRef: ShipmentDetail?._id },
    { skip: !ShipmentDetail?._id }
  );

  // Fetch prices to get the count for the badge
  const [fetchPrices, { data: pricesRes }] = useLazyListShipmentPricesQuery();

  // SMS badge
  const { data: smsLogs } = useGetShipmentMessagesByShipmentIdQuery(
    ShipmentDetail?._id
  );

  useEffect(() => {
    if (!id) return;
    fetchPrices({
      shipmentId: id,
      page: 1,
      limit: 1,
    });
  }, [id, fetchPrices]);

  // Filter prices for customer/vendor visibility if needed
  const filteredPrices = Array.isArray(pricesRes?.data)
    ? pricesRes.data.filter((price) =>
      isCustomer || isVendor ? price.showClient : true
    )
    : [];
  const priceCount = pricesRes?.totalItems || filteredPrices.length || 0;

  // Define tabs with badge counts
  const tabs = [
    {
      label: "Note",
      value: "note",
      badgeCount: filteredNotes.length,
    },
    {
      label: "Transit Branch",
      value: "transits",
      badgeCount: transitBranchCount
    },
    {
      label: "Shipment Routes",
      value: "routes",
      badgeCount: routeCount
    },
    {
      label: "Action",
      value: "action",
      badgeCount: actionCount
    },
    {
      label: "Change Log",
      value: "changeLogs",
      badgeCount: changeLogCount
    },
    {
      label: "Pricing",
      value: "pricing",
      badgeCount: priceCount,
    },
    ...(shipmentRefundDetail?._id
      ? [
          {
            label: "Refund",
            value: "refund",
            badgeCount: "R",
          },
        ]
      : []),
    {
      label: "Document",
      value: "document",
      badgeCount: documents?.totalItems,
    },
    {
      label: "Processing",
      value: "processing",
      badgeCount: ShipmentDetail?.shipmentProcess?.length,
    },
    {
      label: "History",
      value: "history",
      badgeCount: ShipmentDetail?.history?.length,
    },
    {
      label: "Items",
      value: "items",
      badgeCount: ShipmentDetail?.shipmentDetails?.length,
    },
    {
      label: "SMS Log",
      value: "sms-log",
      badgeCount: smsLogs?.totalItems,
    },
  ];

  // Filter tabs if needed
  let filteredTabs = tabs.filter((tab) => {
    if (tab.value === "pricing") {
      // Hide Pricing tab for specific roles if needed
    }
    return true;
  });

  // Ensure activeTab is valid
  const validTabs = filteredTabs.map((tab) => tab.value);
  const validatedActiveTab = validTabs.includes(activeTab) ? activeTab : "note";

  return (
    <>
      <CustomTabs tabs={filteredTabs} activeTab={validatedActiveTab} />

      {ShipmentDetail ? (
        <>
          <CustomTabPanel activeTab={validatedActiveTab} value="history">
            <ShipmentHistoryTab />
          </CustomTabPanel>

          <CustomTabPanel activeTab={validatedActiveTab} value="processing">
            <ShipmentProcessTab />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="routes">
            <ShipmentRoutesTab />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="transits">
            <ShipmentTransitBranchTab />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="note">
            <NoteTab source="Shipment" sourceRef={ShipmentDetail._id} isActive={ShipmentDetail?.isActive} />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="document">
            <DocumentsTab source="Shipment" sourceRef={ShipmentDetail._id} isActive={ShipmentDetail?.isActive} />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="pricing">
            <ShipmentPricesTab
              source="Shipment"
              sourceRef={ShipmentDetail._id}
            />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="action">
            <ShipmentActionTab />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="items">
            <ShipmentItemsTab source="Shipment" />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="sms-log">
            <ShipmentSmsLogTab shipmentId={ShipmentDetail._id} />
          </CustomTabPanel>
          <CustomTabPanel activeTab={validatedActiveTab} value="changeLogs">
            <ShipmentChangeLogs shipmentId={ShipmentDetail._id} />
          </CustomTabPanel>
          {shipmentRefundDetail?._id && (
            <CustomTabPanel activeTab={validatedActiveTab} value="refund">
              <ShipmentRefundTab shipmentId={ShipmentDetail._id} />
            </CustomTabPanel>
          )}
        </>
      ) : (
        <RowsSkeleton />
      )}
    </>
  );
};

export default ShipmentDetailTab;
